import React from 'react';

interface ExistingClient {
  nome_completo: string;
  nome_empresa: string;
  email: string;
  cnpj: string;
  created_at: string;
}

interface DuplicateClientErrorProps {
  existingClient: ExistingClient;
  field: 'email' | 'cnpj';
  onTryAgain: () => void;
  onContact: () => void;
}

const DuplicateClientError: React.FC<DuplicateClientErrorProps> = ({
  existingClient,
  field,
  onTryAgain,
  onContact
}) => {
  const fieldLabel = field === 'email' ? 'e-mail' : 'CNPJ';
  
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="mb-6 p-6 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg 
            className="w-6 h-6 text-amber-500 mt-1" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        </div>
        
        <div className="ml-4 flex-1">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">
            Cliente já cadastrado
          </h3>
          
          <p className="text-amber-700 mb-4">
            Já existe um cliente cadastrado com este {fieldLabel}. 
            Confira os dados do cliente existente abaixo:
          </p>
          
          <div className="bg-white p-4 rounded-lg border border-amber-200 mb-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Dados do cliente existente:
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="space-y-2">
                <div>
                  <span className="font-medium text-gray-600">Nome:</span>
                  <p className="text-gray-900">{existingClient.nome_completo}</p>
                </div>
                
                <div>
                  <span className="font-medium text-gray-600">Empresa:</span>
                  <p className="text-gray-900">{existingClient.nome_empresa || 'Não informado'}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div>
                  <span className="font-medium text-gray-600">E-mail:</span>
                  <p className="text-gray-900 break-all">{existingClient.email}</p>
                </div>
                
                <div>
                  <span className="font-medium text-gray-600">CNPJ:</span>
                  <p className="text-gray-900">{existingClient.cnpj || 'Não informado'}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-200">
              <span className="font-medium text-gray-600">Cadastrado em:</span>
              <p className="text-gray-900">{formatDate(existingClient.created_at)}</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onContact}
              className="flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Entrar em contato
            </button>
            
            <button
              onClick={onTryAgain}
              className="flex items-center justify-center px-4 py-2 bg-white text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50 transition-colors duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Usar dados diferentes
            </button>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Dica:</p>
                <p>
                  Se você é o responsável por este cliente, entre em contato conosco. 
                  Caso contrário, verifique se os dados estão corretos e tente novamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateClientError;
